import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import InGoMap from '../components/maps/InGoMap';
import { useOwnVehicle } from '../components/maps/useOwnVehicle';
import { useDriverOffers } from '../components/driver/DriverOffersProvider';
import {
  formatOfferTime,
  isCashCustomerPayment,
  isWalletCustomerPayment,
  OFFER_RING_CYCLE_MS,
} from '../lib/driverIncomingBookings';
import { apiOfferSecondsLeft } from '../lib/riderWork';
import { useOfferActions } from '../components/driver/useOfferActions';
import { OFFER_KIND_LABEL, OfferPinDrop, OfferPinPickup } from '../components/driver/offerPresentation';
import { readLastDeviceFix } from '../hooks/useLiveLocation';
import { formatDistanceKm, sortOffersByDistance, useOfferDistances } from '../lib/offerProximity';
import { formatGBP } from '../lib/currency';
import { unlockDriverOfferAudio } from '../lib/driverOfferRing';
import { LOGIN_HERO_ART } from '../lib/ingoLogo';
import DriverPermissionPrompts from '../components/driver/DriverPermissionPrompts';
import CreditLimitNotice from '../components/driver/CreditLimitNotice';
import PapersOwedNotice from '../components/driver/PapersOwedNotice';
import OfferActionBar from '../components/driver/OfferActionBar';
import './driverPortal.css';
import './driverHomePremium.css';

function IconPower() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3v8M8.5 8.5a6 6 0 1 0 7 0"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconOnline() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
      <path d="M8 12.5l3 2.5 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * The rider's home: the map on the top 60 % with the rider's own vehicle on
 * it, the header floating on the map, then the status and the offers (Rio,
 * 2 Oct 2026: "after this we do the map thing on the driver's side too"; his
 * answer: their own vehicle only, never other riders). The vehicle stands at
 * the phone's position, turned to its heading: green online, grey offline.
 */
export default function DriverHomePage() {
  const navigate = useNavigate();
  // A screen that sends the rider home may say why ("Job given back…"): shown once.
  const arrival = useLocation().state;
  const [toast, setToast] = useState(() => (typeof arrival?.toast === 'string' ? arrival.toast : ''));
  const {
    online,
    setOnline,
    live,
    offers,
    loadErr,
    ringingOfferKeys,
  } = useDriverOffers();

  const { accept: onAccept, reject: onReject, actionMsg, setActionMsg, busyKey } = useOfferActions();
  /** Re-render every second for countdown / expiry */
  const [, setSecTick] = useState(0);

  useEffect(() => {
    if (!online) return undefined;
    const id = window.setInterval(() => setSecTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [online]);

  // InGo's dispatch chose these offers and closes them itself: the app filters nothing.
  const visibleOffers = online ? offers : [];

  // Nearest pickup first. Unknown distances keep their order after the known ones.
  const driverPos = live.hasFix ? { lat: live.lat, lng: live.lng } : readLastDeviceFix();
  const { distances: offerDistances } = useOfferDistances(visibleOffers, driverPos);
  const sortedOffers = useMemo(
    () => sortOffersByDistance(visibleOffers, offerDistances),
    // offerDistances is rebuilt each render; its content is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleOffers, [...offerDistances.values()].join(',')],
  );

  const offerKey = (o) => `${o.table}:${o.id}`;

  // The home ticks every second while online: the position stays one object until it moves.
  const { vehicle: ownVehicle, position: me } = useOwnVehicle(live);
  const meVehicle = useMemo(() => ({ vehicle: ownVehicle, state: online ? 'free' : 'silent' }), [ownVehicle, online]);

  const handleOnlineSwitch = useCallback(() => {
    setActionMsg('');
    unlockDriverOfferAudio();
    setOnline((was) => !was);
  }, [setOnline, setActionMsg]);

  return (
    <div className="dh dh--premium dh--map" role="main" aria-label="Driver home">
      <div className="dh__srOnly" aria-live="assertive" aria-atomic="true">
        {ringingOfferKeys.size > 0 ? 'New delivery request — respond now.' : ''}
      </div>
      <section className="dh__map" aria-label="Map near you">
        <InGoMap pins={[]} me={me} meVehicle={meVehicle} label="Map near you" height="100%" bleed focusZoom={16} />
        <header className="dh__top dh__top--float">
          <h1 className="dh__brand">
            <img src={LOGIN_HERO_ART} alt="" className="dh__brandRider" decoding="async" />
            <span className="dh__brandText">Driver</span>
          </h1>
          <div className="dh__togR">
            <button type="button" className="dh__chatBtn" onClick={() => navigate('/driver/chat')}>
              Chat
            </button>
            <span className={online ? 'dh__togL dh__togL--g' : 'dh__togL dh__togL--gry'}>{online ? 'Online' : 'Offline'}</span>
            <button
              type="button"
              className={online ? 'dh__sw dh__sw--on' : 'dh__sw'}
              onClick={handleOnlineSwitch}
              role="switch"
              aria-checked={online}
              aria-label={online ? 'Go offline' : 'Go online'}
            >
              <span className="dh__k" aria-hidden />
            </button>
          </div>
        </header>
      </section>

      <div className="dh__sc dh__sc--sheet">
        {toast ? (
          <p className="dh__card" role="status" style={{ margin: '0 0 0.75rem', padding: '0.75rem 1rem', fontWeight: 600 }}>
            {toast}{' '}
            <button type="button" onClick={() => setToast('')} style={{ marginLeft: 8, background: 'none', border: 0, textDecoration: 'underline', color: 'inherit', fontWeight: 700 }}>
              OK
            </button>
          </p>
        ) : null}
        {/* Why no offers come, when the rider owes InGo the credit limit (InGo ADR 0052). */}
        <CreditLimitNotice />
        <PapersOwedNotice />
        <div className={`dh__status dh__card ${online ? 'dh__status--on' : 'dh__status--off'}`}>
          <span className="dh__statusIconWrap" aria-hidden>
            {online ? <IconOnline /> : <IconPower />}
          </span>
          <div className="dh__statusText">
            <h2 className="dh__statusTitle">{online ? 'You are Online' : 'You are Offline'}</h2>
            <p className="dh__statusSub">
              {online ? 'Requests near you appear here and ring your phone.' : 'Go online to receive delivery requests.'}
            </p>
          </div>
        </div>

        {loadErr ? (
          <p className="dh__alert dh__alert--error" role="alert">
            {loadErr}
          </p>
        ) : null}
        {actionMsg ? (
          <p className="dh__alert dh__alert--error" role="status">
            {actionMsg}
          </p>
        ) : null}

        {online &&
          sortedOffers.map((offer) => {
            const k = offerKey(offer);
            const fromYouKm = offerDistances.get(k);
            const busy = busyKey === k;
            // InGo's offers carry their own window; one without it is drawn against a ring cycle.
            const secLeft = apiOfferSecondsLeft(offer);
            const pct = (secLeft / (offer.windowSeconds || OFFER_RING_CYCLE_MS / 1000)) * 100;
            const urgent = secLeft <= 10;
            const isRinging = ringingOfferKeys.has(k);
            const payMethod = String(offer.raw?.payment_method || '').toLowerCase();
            const isWalletPay =
              isWalletCustomerPayment(payMethod) || isWalletCustomerPayment(offer.customerPayment);
            const isCashPay =
              isCashCustomerPayment(payMethod) || isCashCustomerPayment(offer.customerPayment);
            const payLabel = isWalletPay
              ? 'Wallet Payment'
              : isCashPay
                ? 'Cash Payment'
                : offer.customerPayment && offer.customerPayment !== '—'
                  ? offer.customerPayment
                  : null;
            return (
              <div
                key={k}
                className={`dh-offerCard${isRinging ? ' dh-offerCard--ringing' : ''}`}
                role="region"
                aria-label={`New ${OFFER_KIND_LABEL} request`}
              >
                <header className="dh-offerCard__head">
                  <div className="dh-offerCard__headMain">
                    <p className="dh-offerCard__ref">{offer.ref}</p>
                    <p className="dh-offerCard__when">{formatOfferTime(offer.created_at)}</p>
                  </div>
                  <span className="dh-offerCard__badge dh-offerCard__badge--parcel">{OFFER_KIND_LABEL}</span>
                </header>

                <div className="dh-offerCard__route">
                  <div className="dh-offerCard__stop dh-offerCard__stop--pick">
                    <span className="dh-offerCard__pin" aria-hidden>
                      <OfferPinPickup />
                    </span>
                    <div className="dh-offerCard__stopBody">
                      <span className="dh-offerCard__stopLbl">Pickup</span>
                      <p className="dh-offerCard__addr">{offer.from}</p>
                    </div>
                  </div>
                  <div className="dh-offerCard__rail" aria-hidden />
                  <div className="dh-offerCard__stop dh-offerCard__stop--drop">
                    <span className="dh-offerCard__pin" aria-hidden>
                      <OfferPinDrop />
                    </span>
                    <div className="dh-offerCard__stopBody">
                      <span className="dh-offerCard__stopLbl">Drop-off</span>
                      <p className="dh-offerCard__addr">{offer.to}</p>
                    </div>
                  </div>
                </div>

                <p className="dh-offerCard__dist">
                  <span className="dh-offerCard__distIcon" aria-hidden>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
                      <path
                        d="M4 12h3l2 7 4-14 2 7h5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>
                    {offer.dist}
                    {offer.dist && offer.eta ? ' · ' : null}
                    {offer.eta}
                  </span>
                </p>

                {fromYouKm != null ? (
                  <p className="dh-offerCard__dist dh-offerCard__dist--fromYou" role="status">
                    Pickup about {formatDistanceKm(fromYouKm)} from you
                  </p>
                ) : null}

                <p className="dh-offerCard__pkg" role="status">
                  {offer.pkg}
                </p>

                {payLabel ? (
                  <div
                    className={`dh-offerCard__pay${isWalletPay ? ' dh-offerCard__pay--wallet' : ''}${
                      isCashPay ? ' dh-offerCard__pay--cash' : ''
                    }`}
                    role="status"
                  >
                    <div className="dh-offerCard__payTop">
                      <span className="dh-offerCard__payLbl">Payment</span>
                      <span className="dh-offerCard__payVal">{payLabel}</span>
                    </div>
                    {isWalletPay ? (
                      <p className="dh-offerCard__payNote">
                        Customer has already paid through the Ingo Wallet. Do not collect cash.
                      </p>
                    ) : null}
                    {isCashPay ? (
                      <p className="dh-offerCard__payNote dh-offerCard__payNote--cash">
                        Collect cash from the customer at drop-off.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <div className="dh-offerCard__priceBand">
                  <span className="dh-offerCard__priceLbl">Fare</span>
                  <p className="dh-offerCard__amt">{formatGBP(offer.amount)}</p>
                </div>

                <div className={`dh-offerCard__timer${urgent ? ' dh-offerCard__timer--urgent' : ''}`}>
                  <span className="dh-offerCard__timerTxt">Respond in {secLeft}s</span>
                  <div className="dh-offerCard__pb" aria-hidden>
                    <div className="dh-offerCard__pbFill" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                <OfferActionBar
                  offer={offer}
                  busy={busy}
                  disabled={secLeft <= 0}
                  onAccept={() => onAccept(offer)}
                  onDecline={() => onReject(offer)}
                />
              </div>
            );
          })}

        {!loadErr && online && offers.length === 0 && <p className="dh__recentFoot">No requests right now.</p>}
      </div>

      <DriverPermissionPrompts live={live} online={online} />
    </div>
  );
}
